import { FC, useMemo } from "react";
import { IEsimCompetitorComparisonReport } from "../../../interfaces/esim/EsimMargin.types";
import { faceValueNote, isFaceValue, money } from "../../../utils/esim/margin";
import { buildPriceCurve, PriceCurve as Curve } from "../../../utils/esim/priceCurve";
import InfoTip from "../../ui/InfoTip";
import PriceCurveChart from "./PriceCurveChart";

interface Props {
  detail: IEsimCompetitorComparisonReport;
  countryName: string;
}

const HOW_TO_READ =
  "Për çdo sasi, plani më i lirë që jep të paktën aq GB. Çmimet tona në euro; një konkurrent pa kurs " +
  "këmbimi shfaqet në monedhën e vet, pa konvertim. Kaloni kursorin mbi një pikë për planin.";

// The heading's tip, with a line for each seller whose prices are not in euros and each one that
// sells only unlimited plans, which cannot sit on a GB axis.
const tipOf = (curve: Curve): string =>
  [
    HOW_TO_READ,
    ...curve.sellers
      .filter((seller) => isFaceValue(curve.sellerCurrency[seller]))
      .map(
        (seller) =>
          `${seller}: çmimet në ${curve.sellerCurrency[seller]}, pa konvertim; krahasimi (≈) i merr si janë.`
      ),
    ...curve.unlimitedOnly.map(
      (note) =>
        `${note.seller}: vetëm plane pa limit, nga ${money(note.fromPerDay, note.currency)} në ditë` +
        `${faceValueNote(note.currency)}; nuk hyn në kurbë.`
    )
  ].join(" ");

// Us against every competitor in one country, from 1 GB to 100 GB: at each size the cheapest plan
// that gives at least that much data. The like-for-like table above carries the figures.
const PriceCurve: FC<Props> = ({ detail, countryName }) => {
  const curve = useMemo(() => buildPriceCurve(detail), [detail]);
  const drawable = curve.points.some((p) => Object.values(p.offers).some((offer) => offer !== null));

  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <h4 className="text-sm font-semibold">
          Çmimi sipas sasisë në {countryName}: ne kundrejt konkurrentëve, 1 deri 100 GB
        </h4>
        <InfoTip text={tipOf(curve)} />
      </div>
      {drawable ? (
        <PriceCurveChart curve={curve} />
      ) : (
        <p className="text-sm text-neutral">Asnjë plan me sasi dhe çmim për ta krahasuar në këtë shtet.</p>
      )}
    </div>
  );
};

export default PriceCurve;
